import React, { useEffect, useRef, useState } from 'react';
import { User, Loader2 } from 'lucide-react';
import { api, fmtDate } from '../api';

const ready = (f) => (f.localDirectorNumber || '').trim().length === 16 || ((f.lastName || '').trim().length >= 2 && (f.firstName || '').trim().length >= 2);

export const personLine = (p) => [p.taxCode, p.dateOfBirth && `nato/a il ${fmtDate(p.dateOfBirth)}`, p.address?.simpleValue].filter(Boolean).join(' · ');

export default function PeopleSuggest({ form, onPick }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const skip = useRef(false);
  const { lastName, firstName, localDirectorNumber } = form;

  useEffect(() => {
    if (skip.current) { skip.current = false; return undefined; }
    if (!ready(form)) { setItems([]); setOpen(false); return undefined; }
    let alive = true;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/cs/suggest/people', { params: { lastName, firstName, peopleId: localDirectorNumber } });
        if (alive) { setItems(data); setOpen(true); }
      } catch { if (alive) setItems([]); } finally { if (alive) setLoading(false); }
    }, 400);
    return () => { alive = false; clearTimeout(t); };
  }, [lastName, firstName, localDirectorNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (p) => { skip.current = true; setOpen(false); onPick(p); };

  if (loading) return <p className="sm:col-span-2 lg:col-span-4 -mt-2 text-xs text-[#8A8F97] flex items-center gap-2" data-testid="people-suggest-loading"><Loader2 size={13} className="animate-spin text-[#D4AF37]" />Cerco persone…</p>;
  if (!open) return <p className="sm:col-span-2 lg:col-span-4 -mt-2 text-xs text-[#5A5F67]">Scrivi cognome e nome (almeno 2 lettere ciascuno) o il codice fiscale: i suggerimenti compaiono da soli.</p>;
  return (
    <div className="sm:col-span-2 lg:col-span-4 -mt-1 border border-[#1F242C] bg-[#050505] max-h-[420px] overflow-y-auto" data-testid="people-suggest-list">
      {items.length === 0 ? <p className="px-4 py-3 text-sm text-[#5A5F67]" data-testid="people-suggest-empty">Nessuna persona suggerita</p> : items.map((p, i) => (
        <button type="button" key={p.peopleId} onClick={() => pick(p)} className="w-full text-left px-4 py-3 flex items-start gap-3 border-b border-[#1F242C] last:border-0 hover:bg-[#D4AF37]/[0.06] transition-colors" data-testid={`people-suggest-${i}`}>
          <User size={15} className="text-[#D4AF37] mt-0.5 shrink-0" />
          <span className="min-w-0 flex-1">
            <span className="block text-sm text-[#F2F2F2]">{p.firstName} {p.lastName}</span>
            <span className="block text-xs font-mono text-[#5A5F67] truncate">{personLine(p)}</span>
            <span className="block text-xs text-[#8A8F97] mt-1" data-testid={`people-suggest-${i}-roles`}>
              {p.roles?.length ? p.roles.join(' · ') : (p.company_name || 'Cariche visibili con il report dirigente')}
            </span>
          </span>
        </button>
      ))}
    </div>
  );
}
